import React, { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import SearchableDropdown from '../SearchableDropdown';
import DatePicker from '../DatePicker';

// Port of platform-app components/team/QuickFieldList.tsx (RUBY light chrome).
// AI generation stripped — pos-agents is tenant-side; `canUseAI` is accepted
// for prop-contract compatibility but ignored.
//
// super-admin-ui has no FormBuilder, so the FormBuilder types this component
// used are inlined (and exported) here with the same shapes.

// FormBuilder's FieldType union (verbatim):
export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'radio'
  | 'file'
  | 'signature'
  | 'section'
  | 'paragraph';

export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  description?: string;
  defaultValue?: string;
  validation?: {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    pattern?: string;
  };
}

export interface FormTemplate {
  id: string;
  tenantId?: string;
  name: string;
  description?: string;
  category: 'application' | 'agreement' | 'survey' | 'announcement' | 'checklist' | 'other';
  fields: FormField[];
  status: 'draft' | 'active' | 'archived';
  requiresSignature: boolean;
  createdAt: number;
  updatedAt: number;
  createdBy?: string;
}

interface QuickFieldListProps {
  template?: FormTemplate;
  onSave: (t: Partial<FormTemplate>) => void;
  onCancel: () => void;
  /** Ignored — AI features are not available in the staff portal. */
  canUseAI?: boolean;
}

// Category options use FormTemplate.category union values verbatim.
const CATEGORY_OPTIONS: { value: FormTemplate['category']; label: string }[] = [
  { value: 'application', label: 'Application' },
  { value: 'agreement', label: 'Agreement' },
  { value: 'survey', label: 'Survey' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'other', label: 'Other' },
];

// The editor's "display type" — a friendlier picker. Each display type maps to a
// real FieldType for storage. Email/Phone are presentation-only sugar over 'text'.
type DisplayType = 'text' | 'number' | 'date' | 'email' | 'phone' | 'yesno' | 'dropdown';

const DISPLAY_TYPE_OPTIONS: { value: DisplayType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'yesno', label: 'Yes/No' },
  { value: 'dropdown', label: 'Dropdown' },
];

const genId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (_) { /* ignore */ }
  return `field_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
};

// Derive the editor display type from a stored FormField. 'text' fields whose
// placeholder hints at email/phone surface as Email/Phone in the picker.
const toDisplayType = (field: FormField): DisplayType => {
  switch (field.type) {
    case 'number': return 'number';
    case 'date': return 'date';
    case 'checkbox': return 'yesno';
    case 'select': return 'dropdown';
    case 'text':
    default:
      if (field.placeholder?.includes('@')) return 'email';
      if (field.placeholder?.includes('555')) return 'phone';
      return 'text';
  }
};

// Apply a chosen display type back onto a FormField (storage form).
const applyDisplayType = (field: FormField, dt: DisplayType): FormField => {
  const next: FormField = { ...field };
  switch (dt) {
    case 'number': next.type = 'number'; next.placeholder = undefined; next.options = undefined; break;
    case 'date': next.type = 'date'; next.placeholder = undefined; next.options = undefined; break;
    case 'yesno': next.type = 'checkbox'; next.placeholder = undefined; next.options = undefined; break;
    case 'dropdown': next.type = 'select'; next.placeholder = undefined; next.options = next.options ?? []; break;
    case 'email': next.type = 'text'; next.placeholder = 'name@example.com'; next.options = undefined; break;
    case 'phone': next.type = 'text'; next.placeholder = '(555) 555-5555'; next.options = undefined; break;
    case 'text':
    default: next.type = 'text'; next.placeholder = undefined; next.options = undefined; break;
  }
  return next;
};

const blankField = (overrides?: Partial<FormField>): FormField => ({
  id: genId(),
  type: 'text',
  label: '',
  required: false,
  ...overrides,
});

const INPUT_CLASS =
  'w-full px-3 py-2 border border-stone-300 rounded-lg text-sm text-stone-900 bg-white placeholder-stone-400';

const CHIP_CLASS =
  'px-2 py-1 rounded-full bg-white border border-stone-300 text-stone-600 text-[11px] hover:bg-stone-50';

const QuickFieldList: React.FC<QuickFieldListProps> = ({ template, onSave, onCancel }) => {
  const [name, setName] = useState<string>(template?.name || '');
  const [description, setDescription] = useState<string>(template?.description || '');
  const [category, setCategory] = useState<string>(template?.category || 'application');
  const [requiresSignature, setRequiresSignature] = useState<boolean>(template?.requiresSignature || false);
  const [fields, setFields] = useState<FormField[]>(template?.fields || []);

  // --- Field mutations ---
  const updateField = (id: string, updates: Partial<FormField>) => {
    setFields(prev => prev.map(f => (f.id === id ? { ...f, ...updates } : f)));
  };

  const setFieldType = (id: string, dt: DisplayType) => {
    setFields(prev => prev.map(f => (f.id === id ? applyDisplayType(f, dt) : f)));
  };

  const removeField = (id: string) => {
    setFields(prev => prev.filter(f => f.id !== id));
  };

  const addField = (overrides?: Partial<FormField>) => {
    setFields(prev => [...prev, blankField(overrides)]);
  };

  const handleSave = () => {
    onSave({
      name,
      description,
      category: category as FormTemplate['category'],
      fields,
      requiresSignature,
      status: 'active',
    });
  };

  return (
    <div className="space-y-4">
      {/* Header meta */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-stone-700 mb-1">Form name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Direct Deposit Authorization"
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-700 mb-1">Description</label>
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Short description shown to the staff member"
            className={INPUT_CLASS}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <SearchableDropdown
              label="Category"
              options={CATEGORY_OPTIONS}
              value={category}
              onChange={v => setCategory(v || 'application')}
              placeholder="Select category"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-stone-700">
              <input
                type="checkbox"
                checked={requiresSignature}
                onChange={e => setRequiresSignature(e.target.checked)}
                className="accent-admin-primary w-4 h-4"
              />
              Requires signature
            </label>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Builder column */}
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-stone-500 uppercase flex-1">Fields</span>
          </div>

          {fields.length === 0 && (
            <p className="text-sm text-stone-500 py-3 text-center">
              No fields yet. Add one below or use a quick-add chip.
            </p>
          )}

          {fields.map(field => {
            const dt = toDisplayType(field);
            return (
              <div
                key={field.id}
                className="bg-stone-50 border border-stone-200 rounded-lg p-2 space-y-2"
              >
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={field.label}
                    onChange={e => updateField(field.id, { label: e.target.value })}
                    placeholder="Field label"
                    className="flex-1 px-2 py-1.5 border border-stone-300 rounded text-sm text-stone-900 bg-white placeholder-stone-400"
                  />
                  <div className="w-32 flex-shrink-0">
                    <SearchableDropdown
                      compact
                      options={DISPLAY_TYPE_OPTIONS}
                      value={dt}
                      onChange={v => setFieldType(field.id, (v as DisplayType) || 'text')}
                      placeholder="Type"
                    />
                  </div>
                  <label className="text-[11px] text-stone-500 flex items-center gap-1 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={e => updateField(field.id, { required: e.target.checked })}
                      className="accent-admin-primary"
                    />
                    req
                  </label>
                  <button
                    type="button"
                    onClick={() => removeField(field.id)}
                    className="text-red-600 hover:text-red-700 flex-shrink-0 p-1"
                    aria-label="Delete field"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Options editor for dropdown (select) fields */}
                {dt === 'dropdown' && (
                  <input
                    type="text"
                    value={(field.options || []).join(', ')}
                    onChange={e =>
                      updateField(field.id, {
                        options: e.target.value
                          .split(',')
                          .map(o => o.trim())
                          .filter(o => o.length > 0),
                      })
                    }
                    placeholder="Comma-separated options, e.g. Small, Medium, Large"
                    className="w-full px-2 py-1.5 border border-stone-300 rounded text-sm text-stone-900 bg-white placeholder-stone-400"
                  />
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={() => addField()}
            className="w-full border-2 border-dashed border-stone-300 rounded-lg py-2 text-sm text-stone-500 hover:border-stone-400 hover:text-stone-700 mt-1 inline-flex items-center justify-center gap-1"
          >
            <Plus size={14} /> Add field
          </button>

          {/* Quick-add chips */}
          <div className="flex flex-wrap gap-1.5 pt-1 items-center">
            <span className="text-[11px] text-stone-500">Quick add:</span>
            <button
              type="button"
              onClick={() => addField({ label: 'Full name', type: 'text' })}
              className={CHIP_CLASS}
            >
              + Full name
            </button>
            <button
              type="button"
              onClick={() => addField({ label: 'Email', type: 'text', placeholder: 'name@example.com' })}
              className={CHIP_CLASS}
            >
              + Email
            </button>
            <button
              type="button"
              onClick={() => addField({ label: 'Phone', type: 'text', placeholder: '(555) 555-5555' })}
              className={CHIP_CLASS}
            >
              + Phone
            </button>
            <button
              type="button"
              onClick={() => addField({ label: 'Number', type: 'number' })}
              className={CHIP_CLASS}
            >
              + Number
            </button>
            <button
              type="button"
              onClick={() => addField({ label: 'Date', type: 'date' })}
              className={CHIP_CLASS}
            >
              + Date
            </button>
            <button
              type="button"
              onClick={() => addField({ label: 'Yes/No', type: 'checkbox' })}
              className={CHIP_CLASS}
            >
              + Yes/No
            </button>
          </div>
          <p className="text-[11px] text-stone-500 pt-1">
            Field types: Text · Number · Date · Email · Phone · Yes/No · Dropdown
          </p>
        </div>

        {/* Live preview column */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-stone-500 uppercase">Staff member sees</p>
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 space-y-3">
            {fields.length === 0 ? (
              <p className="text-sm text-stone-500">Add fields to preview the form.</p>
            ) : (
              fields.map(field => {
                const dt = toDisplayType(field);
                const labelEl = (
                  <label className="block text-xs font-medium text-stone-700 mb-1">
                    {field.label || 'Untitled field'}{field.required ? ' *' : ''}
                  </label>
                );
                if (dt === 'date') {
                  return (
                    <div key={field.id}>
                      {labelEl}
                      <DatePicker value="" onChange={() => { /* preview only */ }} />
                    </div>
                  );
                }
                if (dt === 'yesno') {
                  return (
                    <div key={field.id}>
                      <label className="flex items-center gap-2 text-sm text-stone-800">
                        <input type="checkbox" className="accent-admin-primary w-4 h-4" disabled />
                        {field.label || 'Untitled field'}{field.required ? ' *' : ''}
                      </label>
                    </div>
                  );
                }
                if (dt === 'dropdown') {
                  return (
                    <div key={field.id}>
                      {labelEl}
                      <SearchableDropdown
                        options={(field.options || []).map(o => ({ value: o, label: o }))}
                        value={null}
                        onChange={() => { /* preview only */ }}
                        placeholder="Select…"
                      />
                    </div>
                  );
                }
                return (
                  <div key={field.id}>
                    {labelEl}
                    <input
                      type={dt === 'number' ? 'number' : 'text'}
                      placeholder={field.placeholder || ''}
                      disabled
                      className={INPUT_CLASS}
                    />
                  </div>
                );
              })
            )}
            <button
              type="button"
              disabled
              className="bg-admin-primary text-white px-4 py-2 rounded-lg text-sm font-semibold w-full opacity-80"
            >
              Submit
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm font-semibold border border-stone-300 text-stone-700 bg-white hover:bg-stone-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="px-4 py-2 rounded-lg text-sm font-semibold bg-admin-primary text-white hover:bg-admin-secondary"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default QuickFieldList;
